import { ManageInvestorDetailsContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/InvestorDetails';
import { revokeInvestmentWorkflow } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import {
	applyVestingStartDateToInvestmentWorkflow,
	applyVestingTerm,
	removeVestingTerm,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
	GetInvestorInvestmentDetailsRes,
	LoanDefinitionType,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useNavigateToMessages } from '@dealstack/frontend/app/pages/Project/MIP/Messages/hooks/useNavigateToMessages';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { getInvestmentTableColumns } from './investmentTableColumns';

interface Props {
	getInvestmentReference: (
		investmentWorkflowType: string,
		isReleased: boolean,
		date: string
	) => string;
	getSecurityTypeTranslation: (type?: SecurityType | LoanDefinitionType) => string;
}

export const useInvestmentTable = ({
	getInvestmentReference,
	getSecurityTypeTranslation,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();
	const { navigateToMessages } = useNavigateToMessages();
	const dataOperations = useMantineTableDataOperations();
	const { mipId } = useRequiredParams('mipId');

	const [view, setView] = useState<'completed' | 'pending'>('completed');
	const [selectedInvestment, setSelectedInvestment] = useState<GetInvestorInvestmentDetailsRes>();
	const [revokeInvestmentId, setRevokeInvestmentId] = useState<string | undefined>();

	const { refetchInvestorValuations } = useContext(ManageInvestorDetailsContext);
	const { vestingTerms, refetchVestingTerms } = useMIPManageContext();

	const {
		investorDetailsData,
		investments,
		investmentsLoading,
		refreshMIPInvestorDetailsData,
		getMIPInvestorContractListRequest,
	} = useContext(ManageInvestorDetailsContext);

	const applyVestingTermMutation = useMutation({
		mutationFn: applyVestingTerm,
	});
	const removeVestingTermMutation = useMutation({
		mutationFn: removeVestingTerm,
	});
	const applyVestingStartDateMutation = useMutation({
		mutationFn: applyVestingStartDateToInvestmentWorkflow,
	});

	const data = useMemo(() => {
		if (view === 'completed' && investments?.investmentDetails?.length) {
			return investments.investmentDetails;
		}

		if (view === 'pending' && investments?.pendingInvestmentDetails?.length) {
			return investments.pendingInvestmentDetails;
		}

		return [];
	}, [investments, view]);

	useEffect(() => {
		if (selectedInvestment && data?.length) {
			const refetchedInvestment = data.find(
				(i) => i.investmentId === selectedInvestment.investmentId
			);
			setSelectedInvestment(refetchedInvestment);
		}
	}, [data]);

	const revokeTsaMutation = useMutation({
		mutationFn: revokeInvestmentWorkflow,

		onSuccess: () => {
			refreshMIPInvestorDetailsData();
			return Promise.all([
				getMIPInvestorContractListRequest.refetch(),
				queryClient.invalidateQueries({
					queryKey: [API_PATHS.MIP_INVESTOR_INVESTORID_DOCUMENT_WORKFLOW_GET],
				}),
			]);
		},
	});

	const onRevokeClick = (investmentId: string) => {
		setRevokeInvestmentId(investmentId);
	};

	const onRevokeDrawerClose = () => {
		setRevokeInvestmentId(undefined);
	};

	const onRevokeConfirm = () => {
		if (revokeInvestmentId) {
			revokeTsaMutation.mutate(revokeInvestmentId);
		}
	};

	const onVestingTermApply = (vestingTermId: string, investmentId: string) => {
		applyVestingTermMutation.mutate(
			{
				vestingTermId,
				investmentWorkflowId: investmentId,
			},
			{
				onSuccess: () => {
					refreshMIPInvestorDetailsData();
					void refetchInvestorValuations();
					showToastV2({
						title: tMipManage('investors.success'),
						state: 'success',
					});
				},
			}
		);
	};

	const onVestingTermRemove = (investmentId: string) => {
		removeVestingTermMutation.mutate(
			{
				investmentWorkflowId: investmentId,
			},
			{
				onSuccess: () => {
					refreshMIPInvestorDetailsData();
					void refetchInvestorValuations();
					showToastV2({
						title: tMipManage('investors.success'),
						state: 'success',
					});
				},
			}
		);
	};

	const onVestingStartDateChange = (investmentWorkflowId: string, startDate: Date | null) => {
		const formattedDate = startDate ? browserDateToMidnightUTC(startDate) : null;

		applyVestingStartDateMutation.mutate(
			{
				investmentWorkflowId,
				startDate: formattedDate,
			},
			{
				onSuccess: () => {
					refreshMIPInvestorDetailsData();
					void refetchInvestorValuations();
					showToastV2({
						title: tMipManage('investors.success'),
						state: 'success',
					});
				},
			}
		);
	};

	useEffect(() => {
		if (
			!investments?.investmentDetails?.length &&
			investments?.pendingInvestmentDetails?.length
		) {
			setView('pending');
		}
	}, [investments]);

	const onMessageClick = () => {
		navigateToMessages({
			investmentWorkflowId: investorDetailsData?.paymentInformation.investmentWorkflowId,
		});
	};

	const columns = useMemo(
		() =>
			getInvestmentTableColumns({
				tMipManage,
				mipId,
				getInvestmentReference,
				getSecurityTypeTranslation,
				onRevokeClick,
				onMessageClick,
			}),
		[
			tMipManage,
			investorDetailsData?.paymentInformation.investmentWorkflowId,
			mipId,
			getInvestmentReference,
			getSecurityTypeTranslation,
		]
	);

	return {
		investorDetailsData,
		data,
		investmentsLoading,
		columns,
		vestingTerms,
		refetchVestingTerms,
		onVestingTermApply,
		onVestingTermRemove,
		onVestingStartDateChange,
		vestingTermUpdateLoading:
			applyVestingTermMutation.isPending ??
			removeVestingTermMutation.isPending ??
			applyVestingStartDateMutation.isPending,
		tMipManage,
		selectedInvestment,
		setSelectedInvestment,
		view,
		setView,
		showViewSwitch: !!investments?.pendingInvestmentDetails?.length,
		dataOperations,
		revokeDrawerOpened: !!revokeInvestmentId,
		onRevokeDrawerClose,
		onRevokeConfirm,
		revokeLoading: revokeTsaMutation.isPending,
		revokeSuccess: revokeTsaMutation.isSuccess,
	};
};
